import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Presentation,
  FileText,
  Target,
  Lightbulb,
  DollarSign,
  Calendar,
  GripVertical,
  Loader2,
  RotateCcw,
  HardDrive,
  Ticket,
  Kanban,
  Shield,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";

// Slide type definitions
export interface SlideConfig {
  title: boolean;
  executiveSummary: boolean;
  keyFindings: boolean;
  recommendations: boolean;
  serviceSummary: boolean;
  assetLifecycle: boolean;
  implementationTracking: boolean;
  budgetPlanning: boolean;
  backupStatus: boolean;
  patchStatus: boolean;
  scheduleNextReview: boolean;
}

export interface PresentationTemplate {
  slides: SlideConfig;
  slideOrder: string[];
}

// Default template configuration
export const DEFAULT_PRESENTATION_TEMPLATE: PresentationTemplate = {
  slides: {
    title: true,
    executiveSummary: true,
    keyFindings: true,
    recommendations: true,
    serviceSummary: true,
    assetLifecycle: true,
    implementationTracking: true,
    budgetPlanning: true,
    backupStatus: true,
    patchStatus: true,
    scheduleNextReview: true,
  },
  slideOrder: [
    "title",
    "executiveSummary",
    "keyFindings",
    "serviceSummary",
    "implementationTracking",
    "recommendations",
    "assetLifecycle",
    "budgetPlanning",
    "backupStatus",
    "patchStatus",
    "scheduleNextReview",
  ],
};

// Slide metadata for display
const SLIDE_METADATA: Record<string, { label: string; description: string; icon: React.ReactNode; required?: boolean }> = {
  title: {
    label: "Title Slide",
    description: "Opening slide with customer name, review period, and organization branding",
    icon: <Presentation className="h-4 w-4" />,
    required: true,
  },
  executiveSummary: {
    label: "Executive Summary",
    description: "High-level overview with alignment scores and key metrics",
    icon: <FileText className="h-4 w-4" />,
  },
  keyFindings: {
    label: "Key Findings",
    description: "Important discoveries from the assessment with severity indicators",
    icon: <Target className="h-4 w-4" />,
  },
  serviceSummary: {
    label: "Service Summary",
    description: "Ticket metrics, priority breakdown, and trends from your PSA integration",
    icon: <Ticket className="h-4 w-4" />,
  },
  recommendations: {
    label: "Recommendations",
    description: "Individual slides for each recommendation with customer approval options",
    icon: <Lightbulb className="h-4 w-4" />,
  },
  assetLifecycle: {
    label: "Asset Lifecycle",
    description: "Hardware and software assets needing attention due to age, warranty, or EOL status",
    icon: <HardDrive className="h-4 w-4" />,
  },
  implementationTracking: {
    label: "Implementation Tracking",
    description: "Kanban board showing the status of all approved recommendations with drag-and-drop",
    icon: <Kanban className="h-4 w-4" />,
  },
  budgetPlanning: {
    label: "Budget Planning",
    description: "Quarterly budget board with drag-and-drop project allocation",
    icon: <DollarSign className="h-4 w-4" />,
  },
  backupStatus: {
    label: "Backup Status",
    description: "Backup statistics and device health overview from your backup integration",
    icon: <Shield className="h-4 w-4" />,
  },
  patchStatus: {
    label: "Patch Status",
    description: "Patch approval and installation status across managed devices",
    icon: <Shield className="h-4 w-4" />,
  },
  scheduleNextReview: {
    label: "Schedule Next Review",
    description: "Final slide to schedule the next business review meeting",
    icon: <Calendar className="h-4 w-4" />,
    required: true,
  },
};

interface PresentationTemplateConfigProps {
  onSave?: (template: PresentationTemplate) => void;
  compact?: boolean;
}

export default function PresentationTemplateConfig({ onSave, compact = false }: PresentationTemplateConfigProps) {
  const { toast } = useToast();
  const [template, setTemplate] = useState<PresentationTemplate>(DEFAULT_PRESENTATION_TEMPLATE);
  const [isDirty, setIsDirty] = useState(false);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  // Fetch current organization settings
  const { data: organization, isLoading } = useQuery<any>({
    queryKey: ["/api/organizations/current"],
    queryFn: () => apiRequest("GET", "/api/organizations/current"),
  });

  // Initialize template from organization settings
  useEffect(() => {
    if (organization?.settings?.presentationTemplate) {
      setTemplate(organization.settings.presentationTemplate);
    }
  }, [organization]);

  // Save template mutation
  const saveMutation = useMutation({
    mutationFn: async (newTemplate: PresentationTemplate) => {
      const response = await apiRequest("PATCH", "/api/organizations/settings", {
        presentationTemplate: newTemplate,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/current"] });
      setIsDirty(false);
      toast({
        title: "Template Saved",
        description: "Your presentation template has been updated. Future presentations will use this configuration.",
      });
      onSave?.(template);
    },
    onError: (error: any) => {
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save presentation template.",
        variant: "destructive",
      });
    },
  });

  const handleSlideToggle = (slideKey: string, enabled: boolean) => {
    const metadata = SLIDE_METADATA[slideKey];
    if (metadata?.required && !enabled) {
      toast({
        title: "Required Slide",
        description: `The ${metadata.label} cannot be disabled.`,
        variant: "destructive",
      });
      return;
    }

    setTemplate((prev) => ({
      ...prev,
      slides: { ...prev.slides, [slideKey]: enabled },
    }));
    setIsDirty(true);
  };

  const handleDragStart = (e: React.DragEvent, slideKey: string) => {
    setDraggedItem(slideKey);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetKey: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetKey) return;

    const newOrder = [...template.slideOrder];
    const draggedIndex = newOrder.indexOf(draggedItem);
    const targetIndex = newOrder.indexOf(targetKey);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    setTemplate((prev) => ({ ...prev, slideOrder: newOrder }));
    setDraggedItem(null);
    setIsDirty(true);
  };

  const handleReset = () => {
    setTemplate(DEFAULT_PRESENTATION_TEMPLATE);
    setIsDirty(true);
  };

  const handleSave = () => {
    saveMutation.mutate(template);
  };

  const enabledSlideCount = Object.values(template.slides).filter(Boolean).length;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading template configuration...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className={compact ? "pb-3" : ""}>
        <CardTitle className="flex items-center gap-2">
          <Presentation className="h-5 w-5" />
          Presentation Template
          {isDirty && <Badge variant="secondary" className="ml-2">Unsaved</Badge>}
        </CardTitle>
        <CardDescription>
          Configure which slides appear in Business Review presentations and their order.
          This template will be used for all future presentations.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Slide Configuration */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Slides ({enabledSlideCount} enabled)</Label>
            <Button variant="ghost" size="sm" onClick={handleReset} className="h-8">
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset
            </Button>
          </div>

          <div className="space-y-2">
            {template.slideOrder.map((slideKey) => {
              const metadata = SLIDE_METADATA[slideKey];
              if (!metadata) return null;

              const isEnabled = template.slides[slideKey as keyof SlideConfig];

              return (
                <div
                  key={slideKey}
                  draggable={!metadata.required}
                  onDragStart={(e) => handleDragStart(e, slideKey)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, slideKey)}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    draggedItem === slideKey
                      ? "opacity-50 border-primary"
                      : isEnabled
                        ? "bg-card hover:bg-muted/50"
                        : "bg-muted/30 opacity-60"
                  } ${!metadata.required ? "cursor-move" : ""}`}
                >
                  {/* Drag Handle */}
                  <div className={`${metadata.required ? "opacity-30" : "opacity-60 hover:opacity-100"}`}>
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                  </div>

                  {/* Icon */}
                  <div className={`${isEnabled ? "text-primary" : "text-muted-foreground"}`}>
                    {metadata.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium text-sm ${!isEnabled && "text-muted-foreground"}`}>
                        {metadata.label}
                      </span>
                      {metadata.required && (
                        <Badge variant="outline" className="text-xs">Required</Badge>
                      )}
                    </div>
                    {!compact && (
                      <p className="text-xs text-muted-foreground truncate">
                        {metadata.description}
                      </p>
                    )}
                  </div>

                  {/* Toggle */}
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={(checked) => handleSlideToggle(slideKey, checked)}
                    disabled={metadata.required}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-between pt-2 border-t">
          <p className="text-xs text-muted-foreground">
            Drag slides to reorder. Changes apply to future presentations.
          </p>
          <Button
            onClick={handleSave}
            disabled={!isDirty || saveMutation.isPending}
            size="sm"
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Template"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// Inline slide configuration panel for use within the presentation dialog
interface SlideConfigPanelProps {
  template: PresentationTemplate;
  customerId?: number;
  customerName?: string;
  onSave: (template: PresentationTemplate) => void;
  onSaveForCustomer?: (template: PresentationTemplate) => Promise<void>;
  onSaveAsDefault?: (template: PresentationTemplate) => Promise<void>;
  onClose: () => void;
}

export function SlideConfigPanel({ 
  template, 
  customerId,
  customerName,
  onSave, 
  onSaveForCustomer,
  onSaveAsDefault, 
  onClose 
}: SlideConfigPanelProps) {
  const { toast } = useToast();
  const [localTemplate, setLocalTemplate] = useState<PresentationTemplate>(template);
  const [isSavingCustomer, setIsSavingCustomer] = useState(false);
  const [isSavingDefault, setIsSavingDefault] = useState(false);
  const [showDefaultConfirm, setShowDefaultConfirm] = useState(false);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  const handleSlideToggle = (slideKey: string, enabled: boolean) => {
    const metadata = SLIDE_METADATA[slideKey];
    if (metadata?.required && !enabled) {
      toast({
        title: "Required Slide",
        description: `The ${metadata.label} cannot be disabled.`,
        variant: "destructive",
      });
      return;
    }

    setLocalTemplate((prev) => ({
      ...prev,
      slides: { ...prev.slides, [slideKey]: enabled },
    }));
  };

  const handleDragStart = (e: React.DragEvent, slideKey: string) => {
    setDraggedItem(slideKey);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetKey: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetKey) return;

    const newOrder = [...localTemplate.slideOrder];
    const draggedIndex = newOrder.indexOf(draggedItem);
    const targetIndex = newOrder.indexOf(targetKey);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    setLocalTemplate((prev) => ({ ...prev, slideOrder: newOrder }));
    setDraggedItem(null);
  };

  const handleSaveForCustomer = async () => {
    if (!onSaveForCustomer) return;
    setIsSavingCustomer(true);
    try {
      await onSaveForCustomer(localTemplate);
      toast({
        title: "Customer Template Saved",
        description: `This configuration will be used for all future ${customerName || 'this customer'}'s presentations.`,
      });
    } catch (error: any) {
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save customer template.",
        variant: "destructive",
      });
    } finally {
      setIsSavingCustomer(false);
    }
  };

  const handleSaveAsDefault = async () => {
    if (!onSaveAsDefault) return;
    setIsSavingDefault(true);
    try {
      await onSaveAsDefault(localTemplate);
      setShowDefaultConfirm(false);
      toast({
        title: "Organization Default Saved",
        description: "This configuration will be used for all future presentations across all customers.",
      });
    } catch (error: any) {
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save as default template.",
        variant: "destructive",
      });
    } finally {
      setIsSavingDefault(false);
    }
  };

  const enabledSlideCount = Object.values(localTemplate.slides).filter(Boolean).length;

  // Confirmation dialog for saving as organization default
  if (showDefaultConfirm) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
          <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
            Set as Organization Default?
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-300">
            This will update the default presentation template for your entire organization. 
            All future presentations (for customers without their own template) will use this configuration.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowDefaultConfirm(false)}>
            Cancel
          </Button>
          <Button 
            size="sm" 
            onClick={handleSaveAsDefault}
            disabled={isSavingDefault}
          >
            {isSavingDefault ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Saving...
              </>
            ) : (
              "Confirm & Save"
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Toggle slides on/off and drag to reorder.
      </p>

      {/* Slide List */}
      <div className="space-y-2 max-h-[350px] overflow-y-auto pr-2">
        {localTemplate.slideOrder.map((slideKey) => {
          const metadata = SLIDE_METADATA[slideKey];
          if (!metadata) return null;

          const isEnabled = localTemplate.slides[slideKey as keyof SlideConfig];

          return (
            <div
              key={slideKey}
              draggable={!metadata.required}
              onDragStart={(e) => handleDragStart(e, slideKey)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, slideKey)}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                draggedItem === slideKey
                  ? "opacity-50 border-primary"
                  : isEnabled
                    ? "bg-card hover:bg-muted/50"
                    : "bg-muted/30 opacity-60"
              } ${!metadata.required ? "cursor-move" : ""}`}
            >
              <div className={`${metadata.required ? "opacity-30" : "opacity-60 hover:opacity-100"}`}>
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>

              <div className={`${isEnabled ? "text-primary" : "text-muted-foreground"}`}>
                {metadata.icon}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-medium text-sm ${!isEnabled && "text-muted-foreground"}`}>
                    {metadata.label}
                  </span>
                  {metadata.required && (
                    <Badge variant="outline" className="text-xs">Required</Badge>
                  )}
                </div>
              </div>

              <Switch
                checked={isEnabled}
                onCheckedChange={(checked) => handleSlideToggle(slideKey, checked)}
                disabled={metadata.required}
              />
            </div>
          );
        })}
      </div>

      <div className="text-xs text-muted-foreground">
        {enabledSlideCount} slides enabled
      </div>

      {/* Action Buttons - Stacked for clarity */}
      <div className="space-y-2 pt-2 border-t">
        <Button className="w-full" size="sm" onClick={() => onSave(localTemplate)}>
          Apply to This Presentation Only
        </Button>
        
        {onSaveForCustomer && customerId && (
          <Button 
            variant="outline" 
            className="w-full" 
            size="sm" 
            onClick={handleSaveForCustomer}
            disabled={isSavingCustomer}
          >
            {isSavingCustomer ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Saving...
              </>
            ) : (
              `Save as Default for ${customerName || 'This Customer'}`
            )}
          </Button>
        )}
        
        {onSaveAsDefault && (
          <Button 
            variant="outline" 
            className="w-full" 
            size="sm" 
            onClick={() => setShowDefaultConfirm(true)}
          >
            Save as Default for All Customers
          </Button>
        )}
        
        <Button variant="ghost" className="w-full" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
